import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiDownload, FiPrinter, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { api } from '../../finance/api/client';
import PayslipDocument, { getDeductionTotal, getGrossTotal, getTakeHome, MONTHS } from '../../finance/pages/payroll/PayslipDocument';

const MyPayslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch both profile and payslips concurrently
      const [profileRes, payrollRes] = await Promise.all([
        api.get('/user/profile'),
        api.get('/finance/payroll/my')
      ]);
      
      const profileData = profileRes.data?.data || profileRes.data || {};
      setUser(profileData);

      const payrollData = payrollRes.data?.data || payrollRes.data?.content || payrollRes.data || [];
      const arrayData = Array.isArray(payrollData) ? payrollData : [];
      // Sort descending by year and month
      const sorted = [...arrayData].sort((a, b) => {
        if (b.year !== a.year) return b.year - a.year;
        return b.month - a.month;
      });
      setPayslips(sorted);
    } catch (error) {
      console.error("Error fetching payslip data:", error);
      toast.error("Failed to load payslips");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 animate-fade-in">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // --- DETAILED PAYSLIP VIEW ---
  if (selectedPayslip && user) {
    return (
      <div className="max-w-5xl mx-auto p-4 sm:p-6 animate-fade-in pb-20">
        
        {/* Special Print Styles to hide everything except the payslip */}
        <style>{`
          @page {
            size: A4 portrait;
            margin: 8mm;
          }
          @media print {
            html, body {
              width: 210mm !important;
              min-height: 297mm !important;
              margin: 0 !important;
              background: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            body * {
              visibility: hidden;
            }
            #printable-payslip, #printable-payslip * {
              visibility: visible;
            }
            #printable-payslip {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 194mm !important;
              min-width: 194mm !important;
              max-width: 194mm !important;
              box-sizing: border-box !important;
              margin: 0 !important;
              padding: 12mm 14mm 8mm !important;
              background: white !important;
              border: none !important;
              box-shadow: none !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print {
              display: none !important;
            }
          }
        `}</style>

        {/* Action Bar (Hidden during print) */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 no-print gap-4">
          <button 
            onClick={() => setSelectedPayslip(null)}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors font-medium shadow-sm w-full sm:w-auto justify-center"
          >
            <FiArrowLeft /> Back to List
          </button>
          
          <div className="flex gap-3 w-full sm:w-auto">
            <button 
              onClick={handlePrint}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold shadow-sm shadow-blue-500/30 transition-colors"
            >
              <FiDownload /> Download PDF
            </button>
          </div>
        </div>

        <PayslipDocument payroll={selectedPayslip} user={user} />
      </div>
    );
  }

  // --- LIST VIEW ---
  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 animate-fade-in pb-20">
      
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <FiDollarSign className="text-blue-600" /> My Payslips
        </h1>
        <p className="text-slate-500 text-sm mt-1">View and download your monthly salary slips.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600 min-w-[700px]">
            <thead className="bg-slate-50/50 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider font-bold">
              <tr>
                <th className="px-6 py-4">Payroll Month</th>
                <th className="px-6 py-4">CTC / Gross</th>
                <th className="px-6 py-4">Deductions</th>
                <th className="px-6 py-4">Take Home</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payslips.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-slate-200">
                      <FiDollarSign className="text-2xl text-slate-400" />
                    </div>
                    <h3 className="text-base font-bold text-slate-800 mb-1">No Payslips Found</h3>
                    <p className="text-slate-500 text-sm">Your payroll history is currently empty.</p>
                  </td>
                </tr>
              ) : (
                payslips.map((slip) => (
                  <tr key={slip.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-800 text-base">{MONTHS[slip.month]} {slip.year}</div>
                      <div className="text-xs text-slate-500 font-medium mt-0.5">Generated: {new Date(slip.createdAt).toLocaleDateString()}</div>
                    </td>
                    <td className="px-6 py-4 font-bold text-slate-700">
                      {formatCurrency(getGrossTotal(slip))}
                    </td>
                    <td className="px-6 py-4 font-bold text-red-600">
                      -{formatCurrency(getDeductionTotal(slip))}
                    </td>
                    <td className="px-6 py-4 font-black text-blue-700 text-base">
                      {formatCurrency(getTakeHome(slip))}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-200">
                        <FiCheckCircle size={12} /> {slip.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={() => setSelectedPayslip(slip)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold text-xs transition-colors"
                      >
                        <FiPrinter /> View Payslip
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyPayslips;
